import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import {round} from "../../constants/Calendar-constants";

const PriceSummary = ({
  roomPrice,
  numNights,
  taxesAndFees,
  couponDiscount = 0,
  couponName = "",
  totalRoomPrice,
  addons = [],
  totalAddonPrice = 0,
  addonTaxAmount = 0,
  promoTax = 0,
  originalTaxesAndFees = 0,
  originalRoomPrice = 0,
  grandTotal,
  currency = "USD",
}) => {
  const safeToFixed = (value, decimals = 2) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : round(value, decimals);
  };

  const isValidPositiveNumber = (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        padding: "24px",
        mt: 3,
        mb: 3,
      }}
    >
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, fontSize: "1.50rem" }}>
        Price Summary
      </Typography>

      {/* Room Price Section */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" mb={1.5}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1rem" }}>
            Room Price ({numNights} night{numNights > 1 ? "s" : ""})
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "1rem" }}>
            {currency}
            {safeToFixed(originalRoomPrice || roomPrice)}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" mb={1.5}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1rem" }}>
            Taxes & Fees
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "1rem" }}>
            {currency}
            {safeToFixed(originalTaxesAndFees || taxesAndFees + (promoTax || 0))} {/* Show original taxes */}
          </Typography>
        </Box>

        {isValidPositiveNumber(couponDiscount) && (
          <Box display="flex" justifyContent="space-between" mb={1.5}>
            <Typography variant="body1" sx={{ color: "#20c997", fontSize: "1rem" }}>
              {couponName || "Coupon / Discount Applied"}
            </Typography>
            <Typography variant="body1" sx={{ color: "#20c997", fontSize: "1rem" }}>
              - {currency}
              {safeToFixed(couponDiscount)}
            </Typography>
          </Box>
        )}

        {/* Show promo tax reduction if applicable */}
        {isValidPositiveNumber(promoTax) && (
          <Box display="flex" justifyContent="space-between" mb={1.5}>
            <Typography variant="body1" sx={{ color: "#20c997", fontSize: "1rem" }}>
              Promo Tax Discount
            </Typography>
            <Typography variant="body1" sx={{ color: "#20c997", fontSize: "1rem" }}>
              - {currency}
              {safeToFixed(promoTax)}
            </Typography>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.125rem" }}>
            Total Room Price
          </Typography>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.125rem" }}>
            {currency}
            {safeToFixed(totalRoomPrice)}
          </Typography>
        </Box>
      </Box>

      {/* Add-ons Section */}
      {addons.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 2, fontSize: "1.25rem" }}
          >
            Add-ons
          </Typography>

          {addons.map((addon, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              mb={1.5}
            >
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1rem" }}>
                {addon.displayName}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                {currency}
                {safeToFixed(addon.totalPrice)}
              </Typography>
            </Box>
          ))}

          {isValidPositiveNumber(addonTaxAmount) && (
            <Box display="flex" justifyContent="space-between" mb={1.5}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1rem" }}>
                Add-on Tax
              </Typography>
              <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                {currency}
                {safeToFixed(addonTaxAmount)}
              </Typography>
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.125rem" }}>
              Total Add-on Price
            </Typography>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.125rem" }}>
              {currency}
              {safeToFixed(totalAddonPrice + addonTaxAmount)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Grand Total */}
      <Divider sx={{ my: 3, borderWidth: 1.5 }} />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: "1.6rem" }}>
          Grand Total
        </Typography>
        <Typography variant="h5" fontWeight="bold" sx={{ color: "#17a2b8", fontSize: "1.6rem" }}>
          {currency}
          {safeToFixed(grandTotal)}
        </Typography>
      </Box>
    </Box>
  );
};

export default PriceSummary;